import * as React from "react";
import * as S from "@styled"
import JSONbig from "json-bigint";
import _ from "lodash";
import urlJoin from "url-join";
import humanizeString from 'humanize-string';

import Toastitfy from "./components/Toastify"

const permissionKey = "frends_permissions";
import { GroupBase } from "react-select";
import { Action, Dispatch } from "redux";
import { ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";
import { IExecutionState, LoopParameter } from "../processDetails/Models";
import { HashMap } from "./model/HashMap";
import { Option, FixedOption } from "./model/Option";
import colors from "./styles/colors";
import instance_running_icon from "../shared/icons/instance_running.svg";
import dehydrated_resume_failed_icon from "../shared/icons/dehydrated_resume_failed.svg";
import AppInsights from "./AppInsights";
import { FeatureFlags } from "./model/FeatureFlags";
import { DehydrationState } from "processInstanceList/model/ProcessInstanceData";

export type ThunkResult<R, S> = ThunkAction<R, S, null, Action>;
export type DispatchType<S> = Dispatch<Action> & ThunkDispatch<S, any, Action>;

// constructor/constructorId are kept as ordinary properties (safe: json-bigint parses into
// Object.create(null), so there is no prototype to pollute); __proto__ is still dropped as
// defense-in-depth against any consumer that later stops using a null-prototype parser.
export const jsonBigWithSafeKeyHandling = JSONbig({ protoAction: "ignore", constructorAction: "preserve" });

export class Utilities {

    public static switchStyles = {
        onColor: "#337ab7",
        offColor: "#ccc"
    };

    public static unlimitedPageSize = 100000000;

    public static setCookie(name: string, value: string, expiryDays: number, domain?: string, path?: string, secure?: boolean) {
        const exdate = new Date()
        exdate.setHours(exdate.getHours() + ((typeof expiryDays !== "number" ? 365 : expiryDays) * 24))
        document.cookie = name + '=' + value +
            ';expires=' + exdate.toUTCString() +
            ';path=' + (path || '/') +
            (domain ? ';domain=' + domain : '') +
            (secure ? ';secure' : '')
    }

    public static deleteCookiesByPrefix(namePrefix: string, domain?: string, path?: string, secure?: boolean) {
        const cookies = this.getCookies();
        // Find cookies by name prefix
        for (const cookieName in cookies) {
            if (cookieName.toLowerCase().startsWith(namePrefix.toLowerCase())) {
                Utilities.setCookie(cookieName, "", -1, domain, path, secure);
            }
        }
    }

    public static getCookies() {
        const cookies: { [key: string]: string } = {};
        for (const cookie of document.cookie.split("; ")) {
            const [name, value] = cookie.split("=");
            cookies[name] = decodeURIComponent(value);
        }
        return cookies;
    }

    public static hasAcceptedCookies(): boolean {
        const cookies = this.getCookies();
        return cookies["cookieconsent_status"] === "allow";
    }

    public static getAllUserPermissions(): HashMap<boolean> {
        const permissionMap = this.getCookies()[permissionKey];
        return permissionMap ? JSON.parse(permissionMap) : {};
    }

    public static getUserPermission(activity: string): boolean {
        const allPermissions = this.getAllUserPermissions();
        return allPermissions[activity];
    }

    public static humanizeLabel(str: string | undefined): string | undefined {
        if (str) {
            return capitalizeLastWordIfId(humanizeString(str));
        }
        return str;
    }

    public static valueInGroupFromId = (opts: GroupBase<Option>[], id: string | number) => {
        const options = opts.flatMap((s) => s.options);
        return options?.find((f) => f.value === id);
    }

    public static valueFromId = (opts: Option[] | null | undefined, id: string | number | undefined | null) => opts?.find((o) => o.value === id);
    public static valuesFromList = (opts: Option[] | null | undefined, ids: string[] | number[]) => opts?.filter((o) => ids.some((id: string | number) => id === o.value));

    public static valueFromListWithFixed = (opts: FixedOption[], ids: string[] | number[] | undefined) => opts.filter((o) => ids?.some((id: any) => id === o.value));

    public static optionsFromValues<T>(values?: T[]): Option<T>[] | undefined {
        return values?.map(value => ({ value, label: String(value) }));
    }

    public static postJsonAndParseResponse<T>(url: string, content: any, signal?: AbortSignal): Promise<T> {
        return Utilities.fetchBase(url, content, "POST", signal).then<T>(this.parseJsonResponse);
    }

    public static async postForm(url: string, formData: FormData): Promise<Response> {
        return await fetch(url, {
            method: "POST",
            credentials: "same-origin",
            body: formData,
            headers: {
                ...await Utilities.getXSRFTokenHeader(),
                "X-Requested-With": "XMLHttpRequest"
            }
        })
    }

    public static putJsonAndParseResponse<T>(url: string, content: any): Promise<T> {
        return Utilities.fetchBase(url, content, "PUT").then<T>(this.parseJsonResponse);
    }

    public static patchJsonAndParseResponse<T>(url: string, content?: any): Promise<T> {
        return Utilities.fetchBase(url, content, "PATCH").then<T>(this.parseJsonResponse);
    }

    public static getQuery(url: string, queryStringParameters?: { [key: string]: string | string[] | number | number[] | boolean | null | undefined }): string {
        let fetchUrl = url;
        if (queryStringParameters) {
            // original from https://stackoverflow.com/a/34209399
            const query = Object.keys(queryStringParameters)
                .filter((k) => queryStringParameters[k] != null)
                .map((k) => {
                    const key = encodeURIComponent(k);
                    const val = queryStringParameters[k];
                    if (Array.isArray(val)) {
                        return key + "=" + val.map((v) => encodeURIComponent(v)).join(`&${key}=`);
                    } else {
                        // val can't be null here
                        return key + "=" + encodeURIComponent(val as string);
                    }
                })
                .join("&");
            if (query) {
                fetchUrl = url + "?" + query;
            }
        }
        return fetchUrl;
    }

    public static getFetchWithoutResultParsing(
        url: string,
        queryStringParameters?: { [key: string]: string | string[] | boolean | number | null | undefined },
        signal?: AbortSignal
    ): Promise<Response> {
        const fetchUrl = this.getQuery(url, queryStringParameters);
        return fetch(fetchUrl, {
            credentials: "same-origin",
            headers: { "X-Requested-With": "XMLHttpRequest" },
            signal
        });
    }

    public static getFetch<T>(
        url: string,
        queryStringParameters?: { [key: string]: string | string[] | boolean | number | null | undefined },
        signal?: AbortSignal
    ): Promise<T> {
        return Utilities.getFetchWithoutResultParsing(url, queryStringParameters, signal)
            .then<T>(Utilities.parseJsonResponse);
    }

    public static getFetchAsText(
        url: string,
        queryStringParameters?: { [key: string]: string | string[] | boolean | number | null | undefined },
        signal?: AbortSignal
    ): Promise<string> {
        return Utilities.getFetchWithoutResultParsing(url, queryStringParameters, signal)
            .then((res) => res.text());
    }

    public static async getFetchWithHeaders<T>(url: string, queryStringParameters?: { [key: string]: string | string[] | null }): Promise<{ content: T, headers: Headers }> {
        try {
            const fetchUrl = this.getQuery(url, queryStringParameters);
            const fetchResponse = await fetch(fetchUrl, {
                credentials: "same-origin",
                headers: { "X-Requested-With": "XMLHttpRequest" }
            });
            const content = await Utilities.parseJsonResponse<T>(fetchResponse);
            return {
                content,
                headers: fetchResponse.headers
            }
        }
        catch (e) {
            console.error(e);
            throw (e);
        }
    }

    public static async deleteFetchAndParseResponse<T>(url: string, queryStringParameters?: { [key: string]: string | string[] | number | number[] | null }, content?: any): Promise<T> {
        const fetchUrl = this.getQuery(url, queryStringParameters);
        return await fetch(fetchUrl, {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                ...await Utilities.getXSRFTokenHeader(),
                "X-Requested-With": "XMLHttpRequest"
            },
            credentials: "same-origin",
            body: content && JSON.stringify(content)
        }).then<T>(this.parseJsonResponse);
    }

    public static parseJsonResponse<T>(response: Response, customErrorParser?: (msg: ProblemDetails) => string | undefined | {}): Promise<T> {
        return response.text()
            .then((content) => {
                if (!response.ok) {
                    let errorContent;
                    try {
                        const errorMsg: ProblemDetails = JSON.parse(content);
                        if (customErrorParser) {
                            // Only used for process build/save errors (compilation, dependency
                            // constraints and whole-graph validation)
                            errorContent = customErrorParser(errorMsg);
                            // An object result (dependency constraint conflict or graph validation
                            // failure) is rejected as-is so the caller's catch can dispatch on its shape
                            if(errorContent instanceof Object) {
                                return Promise.reject(errorContent);
                            }
                        }
                        if (!errorContent) {
                            if (errorMsg.errors != null) {
                                errorContent = Object.keys(errorMsg.errors)
                                    .map((key) => errorMsg.errors && errorMsg.errors[key].map((err) => `• ${err}`).join("\n"))
                                    .join("\n");
                            } else {
                                errorContent = errorMsg.detail ?? errorMsg.title;
                            }
                        }
                        errorContent =
                            `${errorContent} 

${errorMsg.traceId ? `TraceId: ${errorMsg.traceId}` : ''}`
                    } catch (e) {
                        errorContent = content; // ignore, just return as is
                    }
                    return Promise.reject(errorContent);
                } else {
                    // use text just in case the returned value is not valid json (e.g. empty)
                    return Promise.resolve(Utilities.getPossibleJsonFromText(content, response));
                }
            });
    }

    public static getErrorTitle(err: Error): string {
        return _.has(err, "message") ? err.message : "Error";
    }

    public static getErrorText(err: Error & { exceptionMessage?: string, }): string {
        // If error is from backend it usually has an exceptionMessage
        if (_.has(err, "exceptionMessage")) {
            return err.exceptionMessage ?? "Error";
        }

        // If error is from javascript it probably has stack;
        if (_.has(err, "stack")) {
            return err.stack ?? "Error";
        }

        // Test if it has message
        if (_.has(err, "message")) {
            return err.message;
        }

        // If error is a simple string we can return it        
        if (_.isString(err)) {
            return err;
        }

        return "Error";
    }

    public static getStepIdFromParameterKey(loopParameters: LoopParameter[]): string | null {
        if (!loopParameters) {
            return null;
        }
        return loopParameters.map((parameter) => parameter.key + parameter.value).join();
    }

    public static getLoopParameterDictionary(loopParameters: LoopParameter[]): { [loopId: string]: number } {
        const ret: { [key: string]: number } = {};
        if (!loopParameters) {
            return ret;
        }
        for (const l of loopParameters) {
            ret[l.key] = l.value;
        }
        return ret;
    }

    public static XSRFTokenIdentifier = "X-XSRF-Token";

    public static async getXSRFTokenHeader(): Promise<HeadersInit> {
        return {
            [this.XSRFTokenIdentifier]: await this.getXSRFToken()
        }
    }

    public static async getXSRFToken(): Promise<string> {
        const token = await fetch("api/antiforgery/token", {
            method: "GET",
            headers: {
                "X-Requested-With": "XMLHttpRequest"
            },
            credentials: "same-origin"
        });

        if (!token.ok) {
            throw new Error("Could not set XSRF token");
        }

        const xsrfToken = this.getCookies()[this.XSRFTokenIdentifier];
        if (!xsrfToken) {
            throw new Error("XSRF token not found");
        }

        return xsrfToken;
    }

    public static getUrlBase = () => document.getElementsByTagName("base")[0].href;

    public static getBaseUrlPath = (appRootUrl: string): string => {
        const currentPath = document.location.pathname || appRootUrl;
        const startOfAppBase = currentPath.indexOf(appRootUrl);

        let base: string = currentPath;

        if (startOfAppBase !== -1) {
            base = currentPath.substr(0, startOfAppBase);
        }

        base = urlJoin(base, appRootUrl);

        return base;
    };


    /**
     * Navigates the browser to the given path, relative to the declared base path
     * This is needed because IE does not like the base paths...
     * @param to relative path, no leading slash needed
     * @param redirectTimeout timeout in milliseconds before redirecting
     */
    public static navigateRelativeToBasePathUsingTimeout(to: string, redirectTimeout = 1000) {
        const url = this.getUrlBase() + to;
        this.navigateToUsingTimeout(url, redirectTimeout);
    };

    public static navigateToUsingTimeout(to: string, redirectTimeout = 1000) {
        if (this.isValidUrl(to)) {
            setTimeout(() => window.location.href = to, redirectTimeout);
        } else {
            console.error("Failed to navigate to url: " + to);
        }
    };

    public static pushHistoryRelativeToBasePath(to: string) {
        const url = this.getUrlBase() + to;
        if (this.isValidUrl(url)) {
            window.history.pushState("", window.document.title, url);
        } else {
            console.error("Failed to push url to history state: " + url);
        }
    };

    /**
     * In our case, all URLs should be application-controlled, so the risk of XSS here is very minor.
     * If Content Security Policy (CSP) is correctly enforced, this simple client-side validation is redundant.
     */
    public static isValidUrl(url: string) {
        let decodedAndLoweredUrl;
        try {
            decodedAndLoweredUrl = decodeURIComponent(url).toLowerCase();
            return !(decodedAndLoweredUrl.includes("javascript:") || decodedAndLoweredUrl.includes("data:"));
        } catch(e) {
            return false;
        }
    };

    public static toastSuccess(message: string, keepOpen = false, showCloseButton = true) {
        const successIcon = (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 0C4.5 0 0 4.5 0 10C0 15.5 4.5 20 10 20C15.5 20 20 15.5 20 10C20 4.5 15.5 0 10 0ZM15 7.5L9.4 13.1L8.7 13.8L5 10.1L6.4 8.7L8.7 11L13.6 6.1L15 7.5Z" fill={S.Colors.uiGreen} />
            </svg>
        )
        Toastitfy(message, { type: "success", closeButton: showCloseButton, closeOnClick: true, autoClose: keepOpen ? false : 5000, icon: successIcon });
    }

    public static toastWarning(message: string | React.JSX.Element, keepOpen = false, showCloseButton = true, toastId?: string, onClose?: () => void) {
        Toastitfy(message, { type: "warning", closeButton: showCloseButton, closeOnClick: true, autoClose: keepOpen ? false : 8000, toastId: toastId, onClose: onClose, icon: <i className="ci-warning" style={{ color: S.Colors.orangePrimary, fontSize: "24px" }} /> });
    }

    public static toastError(message: string, keepOpen = true, showCloseButton = true) {
        AppInsights.TrackExcepton(message);
        Toastitfy(
            <div style={{ paddingTop: "2px" }}>
                <div style={{
                    WebkitLineClamp: 30,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                    display: "-webkit-box"
                }}>{message}</div>
                <S.Button
                    variant={S.ButtonType.solid}
                    style={{ fontSize: "18px", marginTop: "20px", marginLeft: "-30px" }}
                    onClick={() => navigator.clipboard.writeText(message)}>
                    <S.ButtonIcon className="ci-copy" /> Copy to clipboard
                </S.Button>
            </div>, {
            type: "error", closeButton: showCloseButton,
            closeOnClick: true, autoClose: keepOpen ? false : 8000,
            icon: <i className="ci-off_close" style={{ color: S.Colors.warningDark, fontSize: "24px" }} />
        });
    }

    public static async fetchBase(url: string, content: any, method: string, signal?: AbortSignal) {
        return fetch(url, {
            method: method,
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                ...await Utilities.getXSRFTokenHeader(),
                "X-Requested-With": "XMLHttpRequest"
            },
            credentials: "same-origin",
            body: content && JSON.stringify(content),
            signal
        });
    }

    public static async fetchFeatureFlags(): Promise<FeatureFlags> {
        return Utilities.getFetch<FeatureFlags>("api/feature-flags");
    }
    
    public static arrayToObject<T, K extends keyof T>(array: T[], keyField: K, itemField?: K | null) {
        return array.reduce((obj, item) => {
            obj[item[keyField]] = itemField != null ? item[itemField] : item;
            return obj;
        }, {} as any);
    }

    public static getIconFromExecutionState = (
        executionState: string | number | undefined,
        dehydrationState?: DehydrationState,
        fontSize = 28,
        defaultElement: React.JSX.Element | null = <i className="ci-help_circle_outline" style={{ color: colors.black, fontSize: `${fontSize}px` }} />,
        iconColor?: string
    ): React.JSX.Element | null => {

        if (executionState === undefined) {
            return null;
        }

        const finished = <i className="ci-circle_check_outline icon-finished" style={{ color: colors.uiGreen, fontSize: `${fontSize}px` }} />;
        const started = <img src={instance_running_icon} className="icon-running" style={{ fontSize: `${fontSize}px` }} />;
        const exception = <i className="ci-off_outline_close icon-exception" style={{ color: iconColor ?? colors.warningPrimary, fontSize: `${fontSize}px` }} />;
        const terminatedOrZombie = <i className="ci-error_outline icon-terminated" style={{ color: colors.orangePrimary, fontSize: `${fontSize}px` }} />;
        const dehydrated = <i className="ci-pause_circle_outline icon-dehydrated" style={{ color: colors.bluePrimary, fontSize: `${fontSize}px` }} />;
        const dehydratedResumeFailed = <img src={dehydrated_resume_failed_icon} className="icon-dehydrated-resume-failed" />;

        if (dehydrationState && dehydrationState === DehydrationState.Dehydrated) {
            return dehydrated;
        }
        if (dehydrationState && dehydrationState === DehydrationState.DehydrationProcessFailure) {
            return dehydratedResumeFailed;
        }

        if (typeof executionState === "string") {
            switch (executionState.toLowerCase()) {
                case IExecutionState[IExecutionState.Finished].toLowerCase(): {
                    return finished;
                }
                case IExecutionState[IExecutionState.Started].toLowerCase(): {
                    return started;
                }
                case IExecutionState[IExecutionState.Exception].toLowerCase(): {
                    return exception;
                }
                case IExecutionState[IExecutionState.TerminatedByUser].toLowerCase():
                case IExecutionState[IExecutionState.FinishedAsZombie].toLowerCase(): {
                    return terminatedOrZombie;
                }
                default: {
                    return defaultElement;
                }
            }
        } else if (typeof executionState === "number") {
            switch (executionState) {
                case IExecutionState.Finished: {
                    return finished;
                }
                case IExecutionState.Started: {
                    return started;
                }
                case IExecutionState.Exception: {
                    return exception;
                }
                case IExecutionState.TerminatedByUser:
                case IExecutionState.FinishedAsZombie: {
                    return terminatedOrZombie;
                }
                default: {
                    return defaultElement;
                }
            }
        }

        return defaultElement;
    }

    public static parseFileNameFromContentDisposition(value: string | null) {
        if (value) {
            // This regex is based on this -> https://stackoverflow.com/q/23054475/7007779
            const regex = new RegExp(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/gi);
            return regex.exec(value)?.pop()
        }

        return null;
    }

    public static ensureTrailingSlash(url: string): string {
        if (url.endsWith("/")) {
            return url;
        }
        return url + "/";
    }

    
    public static removeLeadingSlash = (url: string) => {
        if (url.startsWith("/")) {
            return url.substring(1);
        }
        return url;
    }

    private static getPossibleJsonFromText(text: string, response?: Response) {
        if (text.length > 0) {
            try {
                return jsonBigWithSafeKeyHandling.parse(text);
            } catch (e) {
                const contentType = response?.headers?.get("content-type") ?? "";
                // Content that never claimed to be JSON (e.g. a plain-text success body) failing to
                // parse is expected, not an error; anything else should be visible to diagnose.
                if (contentType !== "" && !contentType.toLowerCase().includes("json")) {
                    // tslint:disable-next-line:no-console
                    console.debug("Response not valid json: " + text, e);
                } else {
                    // tslint:disable-next-line:no-console
                    console.error("Response not valid json: " + text, e);
                }
            }
        }
        return text; // just return whatever
    }

    public static getUiVersion() {
        return sessionStorage.getItem("uiVersion") ?? "";
    }

    public static hasProductFruits () {
        return document.getElementById("navigationbar")?.getAttribute("data-has-product-fruits") === "True";
    }

    public static hasFrendsMetrics () {
        return document.getElementById("navigationbar")?.getAttribute("data-has-frends-metrics") === "True";
    }
}

function capitalizeLastWordIfId(s: string) {
    const n = s.split(" ");
    if (n[n.length - 1] === "id") {
        n[n.length - 1] = n[n.length - 1].toUpperCase();
    }
    return n.join(" ");
}

export function isEmptyObject(obj: object) {
    return Object.getOwnPropertyNames(obj).length === 0;
}

export function printWarning(msg: string) {
    if (typeof console !== "undefined") {
        // tslint:disable-next-line:no-console
        console.warn(msg);
    }
}

export function printError(msg: string) {
    if (typeof console !== "undefined") {
        // tslint:disable-next-line:no-console
        console.error(msg);
    }
}
// Taken from https://basarat.gitbooks.io/typescript/docs/types/literal-types.html
export function strEnum<T extends string>(o: T[]): { [K in T]: K } {
    return o.reduce((res, key) => {
        res[key] = key;
        return res;
    }, Object.create(null));
}

export function openInNewTabClick<T>(event: React.MouseEvent<T>): boolean {
    // button = 0 is left click, ctrlKey + left click opens in new tab, shift + left click opens in new window
    if (event.button === 0 && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        return false;
    }
    return true;
}

export interface ProblemDetails {
    type: string;
    title: string;
    status: number;
    traceId: string;
    detail?: string;
    errors?: { [key: string]: Array<string> }
    sourceCode?: string;
    compilationErrors?: Array<string>;
    compilationDiagnostics?: Array<ICompilationDiagnostic>;
    dependencyConstraints?: Array<string>;
    elementId?: string;
    parameterName?: string;
    errorDetails?: Array<string>;
    graphValidationErrors?: Array<IGraphValidationError>;
    /** Message of a validation rejection that belongs to no shape, so it has no elementId. */
    processValidationError?: string;
}

/**
 * A compiler error returned by the server, with the id of the shape whose expression caused it. The id is null
 * for an error in generated code that no shape owns, whose message then keeps its position in the generated
 * source.
 */
export interface ICompilationDiagnostic {
    elementId: string | null;
    message: string;
}

/** A whole-graph validation error returned by the server (ProcessBuilder.Validate()). */
export interface IGraphValidationError {
    code: string;
    message: string;
    elementId?: string;
}

export const getInitialColumnVisibility = <T, >(storeKey: string, optionalLabels: Partial<Record<keyof T, string>>): { [key: string]: boolean } => {
    const colsValueInStore = localStorage.getItem(storeKey);
    const colsValueSet = new Set(JSON.parse(colsValueInStore || '[]'));
    return Object.keys(optionalLabels).reduce((acc, k) => ({ ...acc, [k as string]: colsValueSet.has(k) }), {});
};

export const setVisibleColumnsToStore = (storeKey: string, colunmsKeysToVisibility: { [key: string]: boolean }) => {
    const visibleColumns = Object.keys(colunmsKeysToVisibility).filter((key) => colunmsKeysToVisibility[key]).map((key) => key);
    localStorage.setItem(storeKey, JSON.stringify(visibleColumns));
}

// Base64 decoding that works with UTF-8
export const base64Decode = (encoded: string) => {
    const binString = atob(encoded);
    return new TextDecoder().decode(Uint8Array.from(binString, (m) => m.codePointAt(0)!));
};